import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

import CreateCustomerPage from "./pages/CreateCustomerPage";
import LoginPage from "./pages/Login/LoginPage";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import MonitorPage from "./pages/Monitor/MonitorPage";
import ForgotPasswordPage from "./pages/Login/ForgotPasswordPage";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminCreateUserDevice from "./pages/Admin/AdminCreateUserDevice";
import AdminUsers from "./pages/Admin/AdminUsers";
import AdminDevices from "./pages/Admin/AdminDevices";
import PwaInstallPrompt from "./components/PwaInstallPrompt/PwaInstallPrompt";

function App() {
  const navigate = useNavigate();

  const getStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  };

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token"),
  );

  const [authUser, setAuthUser] = useState(() => {
    return localStorage.getItem("token") ? getStoredUser() : {};
  });

  const [isChecking, setIsChecking] = useState(!!localStorage.getItem("token"));

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        localStorage.removeItem("user");
        setAuthUser({});
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

    const restoreStoredSession = () => {
        const storedUser = getStoredUser();

        if (storedUser?.role) {
          setAuthUser(storedUser);
          setIsAuthenticated(true);
          return true;
        }

        setAuthUser({});
        setIsAuthenticated(false);
        return false;
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        restoreStoredSession();
        setIsChecking(false);
        return;
      }

      const controller = new AbortController();
      const authTimeout = window.setTimeout(() => controller.abort(), 8000);

      try {
        const response = await fetch(
          "https://sfarm.tabangohar.com/api/auth_check.php",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          },
        );

        const data = await response.json();

        if (response.ok && data.status === "success") {
          /*
           * حالت پیشنهادی امن:
           * بهتر است auth_check.php این ساختار را برگرداند:
           *
           * {
           *   status: "success",
           *   user: {
           *     id: 1,
           *     username: "...",
           *     name: "...",
           *     family: "...",
           *     role: "admin" | "customer" | "staff",
           *     parent_user_id: null
           *   }
           * }
           *
           * ولی برای سازگاری با auth_check فعلی که فقط user_id می‌دهد،
           * اگر data.user وجود نداشت، از user ذخیره‌شده در localStorage استفاده می‌کنیم.
           */

          const storedUser = getStoredUser();

          const verifiedUser = data.user
            ? data.user
            : {
                ...storedUser,
                id: data.user_id ?? storedUser.id,
                role: data.role ?? storedUser.role,
              };

          if (!verifiedUser || !verifiedUser.role) {
            console.error("User role not found after auth check");
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setAuthUser({});
            setIsAuthenticated(false);
            return;
          }

          localStorage.setItem("user", JSON.stringify(verifiedUser));
          setAuthUser(verifiedUser);
          setIsAuthenticated(true);
        } else {
          console.error("Session expired or invalid");

          localStorage.removeItem("token");
          localStorage.removeItem("user");

          setAuthUser({});
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Network error:", error);

        /*
         * در خطای شبکه کاربر را بیرون نمی‌اندازیم،
         * اما همچنان role از localStorage خوانده می‌شود.
         * محافظت اصلی ادمین باید در بک‌اند هم انجام شود.
         */
        restoreStoredSession();
      } finally {
        window.clearTimeout(authTimeout);
        setIsChecking(false);
      }
    };

    validateToken();
  }, []);

  const handleLogin = (token, user) => {
    localStorage.setItem("token", token);

    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      setAuthUser(user);
    } else {
      const storedUser = getStoredUser();
      setAuthUser(storedUser);
    }

    setIsAuthenticated(true);

    const role = user?.role || getStoredUser()?.role;

    if (role === "admin") {
      navigate("/admin", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    setAuthUser({});
    setIsAuthenticated(false);

    navigate("/login", { replace: true });
  };

  const getUserRole = () => {
    return authUser?.role || null;
  };

  const isAdmin = () => {
    return getUserRole() === "admin";
  };

  const getHomePath = () => {
    return isAdmin() ? "/admin" : "/";
  };

  const authOnly = (component) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    return component;
  };

  const adminOnly = (component) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (!isAdmin()) {
      return <Navigate to="/" replace />;
    }

    return component;
  };

  if (isChecking) {
    return (
      <div
        style={{
          background: "#0d004c",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
          fontFamily: "IRANSans",
        }}
      >
        در حال تایید هویت...
      </div>
    );
  }

  return (
    <>
      <Routes>
      <Route
        path="/login"
        element={
          !isAuthenticated ? (
            <LoginPage onLogin={handleLogin} />
          ) : (
            <Navigate to={getHomePath()} replace />
          )
        }
      />

      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      <Route
        path="/"
        element={
          isAuthenticated ? (
            isAdmin() ? (
              <Navigate to="/admin" replace />
            ) : (
              <DashboardPage onLogout={handleLogout} />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/admin"
        element={adminOnly(<AdminDashboard onLogout={handleLogout} />)}
      />

      <Route path="/admin/users" element={adminOnly(<AdminUsers />)} />

      <Route
        path="/admin/create-user-device"
        element={adminOnly(<AdminCreateUserDevice />)}
      />

      <Route path="/admin/devices" element={adminOnly(<AdminDevices />)} />

      <Route path="/monitor/:id" element={authOnly(<MonitorPage />)} />

      <Route
        path="/create"
        element={adminOnly(<CreateCustomerPage onLogout={handleLogout} />)}
      />

      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? getHomePath() : "/login"} replace />
        }
      />
      </Routes>
      <PwaInstallPrompt />
    </>
  );
}

export default App;
